import React from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import { MAX_SERIES } from './lensChart';

type Entry = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
};

type LensTooltipProps = {
  /** Supplied by recharts. */
  active?: boolean;
  payload?: Array<Entry>;
  label?: unknown;
  formatLabel: (value: unknown) => string;
  formatValue: (value: unknown, key: string) => string;
  /** Stacked cards get a total: it is the number the stack is showing. */
  showTotal?: boolean;
  /** Share of the whole per name, for a form whose point is the proportion. */
  shares?: Record<string, number>;
  maxRows?: number;
};

// Every plotted series has to fit, since the spec calls for every series at
// the hovered x: the palette never assigns more than MAX_SERIES slots, so
// capping the tooltip at the same number never truncates a normal chart.
const DEFAULT_MAX_ROWS = MAX_SERIES;

/**
 * The default tooltip prints one large, series-coloured line per key in the
 * order they were plotted, which on a stacked card of ten categories overflows
 * the card and buries the largest value in the middle.
 *
 * This one ranks by value, keeps the text in ink so the swatch carries
 * identity, and caps the rows it draws.
 */
export default function LensTooltip({
  active,
  payload,
  label,
  formatLabel,
  formatValue,
  showTotal,
  shares,
  maxRows = DEFAULT_MAX_ROWS,
}: LensTooltipProps) {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = payload
    .filter((entry) => entry.value !== null && entry.value !== undefined)
    .map((entry) => ({
      key: String(entry.dataKey ?? entry.name ?? ''),
      name: String(entry.name ?? entry.dataKey ?? ''),
      value: Number(entry.value),
      color: entry.color,
    }))
    .filter((entry) => Number.isFinite(entry.value));

  if (!rows.length) {
    return null;
  }

  // Largest first: on a stack the big contributors are what the reader came for.
  const ranked = [...rows].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const shown = ranked.slice(0, maxRows);
  const hidden = ranked.length - shown.length;
  const total = ranked.reduce((sum, entry) => sum + entry.value, 0);
  // The total's format has to come from a key that does not move with the
  // hover position. rows keeps payload order, which is fixed by how the
  // series were plotted, not by which one is largest at this particular x.
  const totalFormatKey = rows[0].key;

  return (
    <Paper elevation={3} sx={{ maxWidth: 280, px: 1.25, py: 1 }}>
      <Typography sx={{ display: 'block', fontWeight: 600, mb: 0.5 }} variant="caption">
        {formatLabel(label)}
      </Typography>

      <Stack spacing={0.25}>
        {shown.map((entry) => (
          <Stack alignItems="center" direction="row" key={entry.key} spacing={0.75}>
            {/* The swatch carries identity, so the text can stay legible ink. */}
            <Box sx={{ bgcolor: entry.color, borderRadius: '8px', flexShrink: 0, height: 8, width: 8 }} />
            <Typography color="text.secondary" noWrap sx={{ flexGrow: 1, minWidth: 0 }} variant="caption">
              {entry.name}
            </Typography>
            <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }} variant="caption">
              {formatValue(entry.value, entry.key)}
              {shares?.[entry.name] === undefined ? '' : ` · ${Math.round(shares[entry.name] * 100)}%`}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {hidden > 0 && (
        <Typography color="text.secondary" sx={{ display: 'block', mt: 0.25 }} variant="caption">
          +{hidden} more
        </Typography>
      )}

      {showTotal && ranked.length > 1 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography color="text.secondary" variant="caption">
              Total
            </Typography>
            <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }} variant="caption">
              {formatValue(total, totalFormatKey)}
            </Typography>
          </Stack>
        </>
      )}
    </Paper>
  );
}

type LensSeriesEndLabelProps = {
  /** Supplied by recharts from the point's own geometry. */
  x?: number;
  y?: number;
  index?: number;
  /** Point count on the line, so only the true final point draws a label. */
  totalPoints: number;
  name: string;
  color?: string;
};

/**
 * A direct end-of-line label for a series, per the legend rule: with four or
 * fewer series a label beside the line reads faster than a legend key. Recharts
 * calls a line's label renderer once per point, so every call before the last
 * is a no-op and the name is drawn once, beside the segment it names.
 */
export function LensSeriesEndLabel({ x, y, index, totalPoints, name, color }: LensSeriesEndLabelProps) {
  if (x === undefined || y === undefined || index !== totalPoints - 1) {
    return null;
  }
  return (
    <text dominantBaseline="middle" fill={color ?? 'currentColor'} fontSize={11} x={x + 6} y={y}>
      {name}
    </text>
  );
}
